import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { processCoreModelResponse } from "@/store/-utils";
import ProductSetsApi from "@/store/product-sets/-api";
import {
  type ProductSet,
  type ProductSetFeed,
  type ProductSetFeeds,
  type ProductSetMap,
  type ProductSetMeta,
  type ProductSetProductsMap,
} from "@/store/product-sets/types";

const emptyProductSetFactory: () => ProductSetFeed = () => ({ ids: [], meta: null });

@Module({
  name: "product-sets",
  stateFactory: true,
  namespaced: true,
})
export default class ProductSetsStore extends VuexModule {
  productSets: ProductSetMap = {};
  productSetProducts: ProductSetProductsMap = {};
  feeds: ProductSetFeeds = {};

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.productSetFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feed = this.productSetFeed(feedName);
      return feed && feed.meta !== null && feed.ids.length > 0;
    };
  }

  get productSetFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyProductSetFactory();
  }

  @Action({ rawError: true })
  async fetchProductSet({ id }: { id: string }) {
    const api = new ProductSetsApi(this.store.$axios);
    const result = await api.getProductSetInfo({ id });
    processCoreModelResponse(this.store, result.data);
  }

  @Action({ rawError: true })
  async fetchProductSetsByProfile({
    displayName,
    profileId,
    limit,
    overwrite = false,
    giftGuideFF,
  }: {
    displayName: string;
    profileId: string;
    limit?: number;
    overwrite?: boolean;
    giftGuideFF?: boolean;
  }) {
    if (!profileId) {
      throw new Error(`No profile id found for display name ${displayName}`);
    }

    if (!this.getFeedHasMorePages(displayName) && overwrite === false) {
      return;
    }

    const api = new ProductSetsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getProductSetsByProfile"]>[0] = { profileId, limit };
    const meta = this.feeds[displayName]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const response = await api.getProductSetsByProfile(data);

    await processCoreModelResponse(this.store, response.data);
    let productCollections = response.data.shopProductCollections;

    if (giftGuideFF) {
      productCollections = productCollections.filter((product) => {
        return product.subtype !== "GIFT_GUIDE";
      });
    }
    const ids = productCollections.map((item) => item.collectionId);
    this.setProductSetFeed({ feedName: displayName, ids, meta: response.data.meta, overwrite });
  }

  @Action({ rawError: true })
  async fetchProductSetProducts({ id, overwrite = false }: { id: string; overwrite?: boolean }) {
    if (!this.getFeedHasMorePages(id) && overwrite === false) {
      return;
    }

    const api = new ProductSetsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getProductSetProducts"]>[0] = { id };
    const meta = this.feeds[id]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const result = await api.getProductSetProducts(data);

    const productSetProducts = result.data.shopProducts;

    if (productSetProducts.length > 0) {
      await processCoreModelResponse(this.store, result.data);

      this.setProductSetFeed({
        feedName: id,
        ids: productSetProducts.map((product) => product.shopProductId),
        meta: result.data.meta || null,
        overwrite,
      });
    }
  }

  @Mutation
  setProductSet({ productSetId, productSet }: { productSetId: string; productSet: ProductSet }) {
    Vue.set(this, "productSets", Object.assign({}, this.productSets, { [productSetId]: productSet }));
  }

  @Mutation
  setManyProductSets({ productSets }: { productSets: ProductSetMap }) {
    Vue.set(this, "productSets", Object.assign({}, this.productSets, productSets));
  }

  @Mutation
  setProductSetProducts({ productSetProducts }: { productSetProducts: ProductSetProductsMap }) {
    Vue.set(this, "productSetProducts", Object.assign({}, this.productSetProducts, productSetProducts));
  }

  @Mutation
  setProductSetFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: ProductSetMeta | null;
    overwrite?: boolean;
  }) {
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, { ids: [], meta: null });
    }

    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }
}

export const getProductSetsStore = (store: Store<unknown>) => {
  return getModule(ProductSetsStore, store);
};
